import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import AcademyExam from "@/components/AcademyExam";
import AcademySuggest from "@/components/AcademySuggest";
import { academyApi } from "@/lib/academy";

/** The official comprehensive exam — every vocabulary term plus knowledge from every belt. Study mode first. */
export default function AcademyFinal() {
  const [state, setState] = useState({ phase: "loading", lockedReason: "", vocab: [], meta: null, certToken: null });

  async function check() {
    try {
      const d = await academyApi.dashboard();
      if (d.comprehensive?.unlocked) {
        const g = await academyApi.glossary();
        setState((s) => ({ ...s, phase: "ready", vocab: g.terms || [] }));
      } else {
        setState((s) => ({ ...s, phase: "locked", lockedReason: "The official exam opens when your Black Belt is earned." }));
      }
    } catch (e) {
      setState((s) => ({ ...s, phase: e.response?.status === 401 ? "auth" : "error" }));
    }
  }
  useEffect(() => { check(); }, []);

  async function start() {
    try {
      const res = await academyApi.startFinal();
      setState((s) => ({ ...s, phase: "exam", meta: res, examDone: null }));
    } catch (e) {
      setState((s) => ({ ...s, notice: e.response?.data?.detail || "Could not start." }));
    }
  }

  return (
    <>
      <Seo title="The official comprehensive exam | Solar Black Belt Academy" description="The final Solar Black Belt Academy exam: every vocabulary term plus knowledge from all eight belts. Study mode, then the graded test — pass to earn the Certified Solar & Powerwall Professional internal credential." />
      <section className="page-hero"><div className="wrap">
        <span className="eyebrow">THE OFFICIAL TEST</span>
        <h1>The comprehensive exam.</h1>
        <p className="lead">Every vocabulary word from all 24 lessons, plus knowledge from every belt — this is the test that certifies you for the solar and Tesla Powerwall business.</p>
      </div></section>

      <section className="wrap academy-section" style={{ maxWidth: 860 }} data-testid="academy-final">
        {state.phase === "loading" && <p>Loading…</p>}
        {state.phase === "auth" && (
          <div className="academy-exam"><h3>Sign in first</h3>
            <p className="micro">The exam is part of the free academy account.</p>
            <Link className="btn lime" to="/academy/login" state={{ from: "/academy/final" }}>Sign in ↗</Link></div>
        )}
        {state.phase === "locked" && (
          <div className="academy-exam" data-testid="academy-final-locked">
            <h3>🔒 {state.lockedReason}</h3>
            <p className="micro">Keep training — every lesson you pass gets you closer. <Link to="/academy/dashboard">Back to my dashboard →</Link></p>
          </div>
        )}
        {state.phase === "ready" && !state.meta && (
          <>
            <div className="academy-exam">
              <h3>How it works</h3>
              <p className="micro">1. Study every vocabulary term below (click a card to check yourself). 2. Take the exam: every vocabulary word plus knowledge questions from all eight belts. 3. Score 85%+ with every safety-critical item correct to earn the <strong>Certified Solar &amp; Powerwall Professional</strong> internal credential — issued automatically, signed by Lawrence Williams.</p>
              <button className="btn lime" data-testid="academy-final-start" onClick={start}>Start the official exam ↗</button>
              {state.notice && <p className="micro" style={{ color: "#a33" }}>{state.notice}</p>}
            </div>
            <h3 style={{ marginTop: 26 }}>Study mode — tap a term, guess, then check yourself</h3>
            <div className="academy-vocab-grid" data-testid="academy-final-study">
              {(state.vocab.length ? state.vocab : []).map((v) => (
                <details key={v.id} className="academy-choice" style={{ flexDirection: "column" }}>
                  <summary style={{ cursor: "pointer", fontWeight: 700 }}>{v.term}</summary>
                  <p className="micro" style={{ margin: "8px 0 0" }}>{v.definition}</p>
                </details>
              ))}
            </div>
          </>
        )}
        {state.phase === "exam" && state.meta && (
          <>
            <p className="micro">{state.meta.total} questions · {state.meta.vocabularyCount} vocabulary · threshold 85%</p>
            <AcademyExam scope={{ kind: "final" }} onPassed={(res) => {}} onClose={() => check()} />
          </>
        )}
        <AcademySuggest page="/academy/final" />
      </section>
    </>
  );
}
